// frontend/src/theme/ThemeContext.tsx
// -----------------------------------------------------------------------------
// Theme Context & Provider
// -----------------------------------------------------------------------------
// Manages theme switching with localStorage persistence
// Provides theme selection to entire app via React Context
// -----------------------------------------------------------------------------

import { ThemeProvider as MuiThemeProvider, CssBaseline, Theme } from "@mui/material";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import athenaGradientTheme from "./variants/athena_gradient";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type ThemeVariant = "athena_gradient";

interface ThemeContextValue {
  /** Current theme variant name */
  themeVariant: ThemeVariant;
  /** Switch to a different theme */
  setThemeVariant: (variant: ThemeVariant) => void;
  /** Current MUI theme object */
  theme: Theme;
  /** Available theme options */
  themeOptions: { value: ThemeVariant; label: string; description: string }[];
}

// -----------------------------------------------------------------------------
// Theme Map
// -----------------------------------------------------------------------------

const themeMap: Record<ThemeVariant, Theme> = {
  athena_gradient: athenaGradientTheme,
};

const themeOptions: ThemeContextValue["themeOptions"] = [
  {
    value: "athena_gradient",
    label: "Athena Gradient",
    description: "Soft pastel gradient with dark text",
  },
];

// -----------------------------------------------------------------------------
// Storage Key
// -----------------------------------------------------------------------------

const THEME_STORAGE_KEY = "athena-theme-variant";

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Optional default theme (defaults to "classic") */
  defaultTheme?: ThemeVariant;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = "athena_gradient",
}) => {
  // Initialize from localStorage or default — always athena_gradient now
  const [themeVariant, setThemeVariantState] = useState<ThemeVariant>(() => {
    return "athena_gradient";
  });

  // Persist to localStorage when changed
  const setThemeVariant = (variant: ThemeVariant) => {
    setThemeVariantState(variant);
    if (typeof window !== "undefined") {
      localStorage.setItem(THEME_STORAGE_KEY, variant);
    }
  };

  // Get current theme object
  const theme = useMemo(() => themeMap[themeVariant], [themeVariant]);

  // Update document attribute for potential CSS selectors
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeVariant);
  }, [themeVariant]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      themeVariant,
      setThemeVariant,
      theme,
      themeOptions,
    }),
    [themeVariant, theme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export const useThemeContext = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
};

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

export default ThemeProvider;
